import {
  FLOW_EDGE_PORTS,
  isFlowTerminalNode,
  type FlowEdge,
  type FlowNode,
} from "./contracts";

/**
 * Utilitários puros de grafo compartilhados por normalização, validador,
 * compilador e simulador. Manter uma única fonte evita que cada camada invente
 * a sua própria ordem, alcançabilidade ou detecção de ciclo.
 */

export interface FlowGraphIndex {
  nodes: FlowNode[];
  edges: FlowEdge[];
  nodeById: Map<string, FlowNode>;
  outgoing: Map<string, FlowEdge[]>;
  incoming: Map<string, FlowEdge[]>;
}

const portRank = new Map(FLOW_EDGE_PORTS.map((port, index) => [port, index] as const));

/** Ordem canônica dos nós: identidade estável, independente do desenho. */
export function canonicalNodeOrder(nodes: readonly FlowNode[]): FlowNode[] {
  return [...nodes].sort((left, right) => left.id.localeCompare(right.id));
}

/**
 * Ordem canônica das arestas: origem, porta, prioridade e ID. A prioridade
 * decide a avaliação; o ID desempata para que a serialização seja estável.
 */
export function canonicalEdgeOrder(edges: readonly FlowEdge[]): FlowEdge[] {
  return [...edges].sort((left, right) => {
    if (left.fromNodeId !== right.fromNodeId) return left.fromNodeId.localeCompare(right.fromNodeId);
    const leftPort = portRank.get(left.fromPort) ?? Number.MAX_SAFE_INTEGER;
    const rightPort = portRank.get(right.fromPort) ?? Number.MAX_SAFE_INTEGER;
    if (leftPort !== rightPort) return leftPort - rightPort;
    if (left.priority !== right.priority) return left.priority - right.priority;
    return left.id.localeCompare(right.id);
  });
}

export function buildFlowGraphIndex(
  nodes: readonly FlowNode[],
  edges: readonly FlowEdge[],
): FlowGraphIndex {
  const orderedNodes = canonicalNodeOrder(nodes);
  const orderedEdges = canonicalEdgeOrder(edges);
  const nodeById = new Map(orderedNodes.map((node) => [node.id, node] as const));
  const outgoing = new Map<string, FlowEdge[]>();
  const incoming = new Map<string, FlowEdge[]>();
  for (const node of orderedNodes) {
    outgoing.set(node.id, []);
    incoming.set(node.id, []);
  }
  for (const edge of orderedEdges) {
    if (outgoing.has(edge.fromNodeId)) outgoing.get(edge.fromNodeId)?.push(edge);
    if (incoming.has(edge.toNodeId)) incoming.get(edge.toNodeId)?.push(edge);
  }
  return { nodes: orderedNodes, edges: orderedEdges, nodeById, outgoing, incoming };
}

export function outgoingEdges(index: FlowGraphIndex, nodeId: string): FlowEdge[] {
  return index.outgoing.get(nodeId) ?? [];
}

/** Conjunto alcançável a partir de um nó, seguindo somente arestas válidas. */
export function reachableFrom(index: FlowGraphIndex, startId: string): Set<string> {
  const seen = new Set<string>();
  if (!index.nodeById.has(startId)) return seen;
  const stack = [startId];
  while (stack.length > 0) {
    const current = stack.pop() as string;
    if (seen.has(current)) continue;
    seen.add(current);
    for (const edge of outgoingEdges(index, current)) {
      if (index.nodeById.has(edge.toNodeId) && !seen.has(edge.toNodeId)) stack.push(edge.toNodeId);
    }
  }
  return seen;
}

export function terminalNodeIds(index: FlowGraphIndex): string[] {
  return index.nodes.filter((node) => isFlowTerminalNode(node.type)).map((node) => node.id);
}

/** Verdadeiro quando algum terminal é alcançável a partir do nó informado. */
export function reachesTerminal(index: FlowGraphIndex, nodeId: string): boolean {
  const reachable = reachableFrom(index, nodeId);
  return index.nodes.some((node) => isFlowTerminalNode(node.type) && reachable.has(node.id));
}

export interface FlowCycle {
  /** Aresta de retorno que fecha o ciclo. É nela que o limite é declarado. */
  backEdgeId: string;
  /** Nós que compõem o ciclo, do destino da aresta de retorno até a origem. */
  nodeIds: string[];
}

/**
 * Ciclos detectados por busca em profundidade determinística a partir do
 * `start`. Cada aresta que aponta para um nó ainda na pilha fecha um ciclo.
 */
export function findFlowCycles(index: FlowGraphIndex, startId: string): FlowCycle[] {
  const cycles: FlowCycle[] = [];
  if (!index.nodeById.has(startId)) return cycles;
  const visited = new Set<string>();
  const stackIds: string[] = [];
  const onStack = new Set<string>();

  const visit = (nodeId: string) => {
    visited.add(nodeId);
    stackIds.push(nodeId);
    onStack.add(nodeId);
    for (const edge of outgoingEdges(index, nodeId)) {
      if (!index.nodeById.has(edge.toNodeId)) continue;
      if (onStack.has(edge.toNodeId)) {
        const start = stackIds.indexOf(edge.toNodeId);
        cycles.push({ backEdgeId: edge.id, nodeIds: stackIds.slice(start) });
        continue;
      }
      if (!visited.has(edge.toNodeId)) visit(edge.toNodeId);
    }
    stackIds.pop();
    onStack.delete(nodeId);
  };

  visit(startId);
  return cycles;
}

/** IDs das arestas que fecham algum ciclo alcançável a partir do `start`. */
export function cycleClosingEdgeIds(index: FlowGraphIndex, startId: string): Set<string> {
  return new Set(findFlowCycles(index, startId).map((cycle) => cycle.backEdgeId));
}
